import {
  CheckboxWithLabel, DropdownWithLabel,
  memoWithOpen,
  SimpleDialog,
  TextWithLabel
} from "../../components";
import React, {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {useTaskQueue} from "../../hooks";
import { FormHelperText, Grid, Theme } from "@mui/material";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {deleteAsset} from "../../redux/assets-slice";
import { useTranslation } from "react-i18next";
import { useAssetTools } from "../../utilities";
import { FunctionProps } from "./props";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    helperText: {
      textAlign: "center",
      paddingTop: theme.spacing(2)
    },
    savedLocation: {
      wordWrap: "break-word"
    }
  }),
);

export const ConvertUnits = memoWithOpen((props: FunctionProps) => {
  const {assets, open, onClose} = props;

  const dispatch = useDispatch();
  const {addRunningTask} = useTaskQueue();
  const classes = useStyles();
  const {t} = useTranslation();
  const {addAssetPaths} = useAssetTools();

  const [inputUnits, setInputUnits] = useState('m');
  const [outputUnits, setOutputUnits] = useState('ft');
  const [importResult, setImportResult] = useState(true);

  const nullUnits = inputUnits === '' || outputUnits === '';
  const sameUnits = (inputUnits === outputUnits && !nullUnits);
  const canSubmit = (!sameUnits && !nullUnits);


  // TODO: actually handle multiple inputs (sent multiple exe commands to processing queue)
  const asset = assets ? assets[0] : undefined;

  // Get name of saved file for display
  const basename = asset.name.replace(/\.[^/.]+$/, "");
  const pathNameNoExt = asset.path.replace(/\.[^/.]+$/, "");
  const outputName = `${basename}_${inputUnits}_to_${outputUnits}.las`;
  const outputPath = `${pathNameNoExt}_${inputUnits}_to_${outputUnits}.las`;

  const handleSubmit = () => {
    const pythonName = "convert_units";

    let commands = [
      "-p", pythonName,
      "--inputlas", asset.path,
      "--input_units", inputUnits,
      "--output_units", outputUnits
    ];

    const onFinish = async () => {
      if (!importResult) return;

      // Remove old LAS
      dispatch(deleteAsset(asset.id));

      // Add new thinned LAS
      await addAssetPaths([outputPath], asset.folderID);
    };

    onClose();
    addRunningTask(commands, onFinish);
  };

  const resetDefaults = () => {
    setInputUnits('m');
    setOutputUnits('ft');
    setImportResult(true);
  };

  useEffect(() => {
    if (open) return;
    resetDefaults();
  }, [open]);

  return (
    <React.Fragment>
      <SimpleDialog
        open={open}
        onClose={onClose}
        onSubmit={handleSubmit}
        canSubmit={canSubmit}
        title={t('functions.convert_units.title')}
        button={t('buttons.submit')}
      >
        <Grid container spacing={2}>

          {/** Input Units */}
          <Grid item xs={6}>
            <DropdownWithLabel
              title={t('functions.convert_units.input_units')}
              label={t('functions.convert_units.input_units_description')}
              onChange={setInputUnits}
              value={inputUnits}
              options={[
                ["m", t('units.meters')],
                ["ft", t('units.feet')],
                ["sft", t('units.survey-feet')]
              ]}
            />
          </Grid>

          {/** Output Units */}
          <Grid item xs={6}>
            <DropdownWithLabel
              title={t('functions.convert_units.output_units')}
              label={t('functions.convert_units.output_units_description')}
              onChange={setOutputUnits}
              value={outputUnits}
              options={[
                ["m", t('units.meters')],
                ["ft", t('units.feet')],
                ["sft", t('units.survey-feet')]
              ]}
            />
          </Grid>

          {/** Import results */}
          <Grid item xs={6}>
            <CheckboxWithLabel
              title={t('functions.import_results')}
              label={t('functions.convert_units.converted_file_is_imported')}
              value={importResult}
              onChange={setImportResult}
            />
          </Grid>

          {/** Output Path Display */}
          {canSubmit && <Grid item xs={6}>
            <TextWithLabel
              title={t('functions.convert_units.generated_output_location')}
              label={outputName}
            />
          </Grid>}

        </Grid>
        {sameUnits && <FormHelperText className={classes.helperText} error>
          {t('functions.convert_units.same_units_error')}
        </FormHelperText>}
      </SimpleDialog>
    </React.Fragment>
  );
});
